<template>
  <div class="menu-content">
    <div class="menu-links" :class="{ 'menu-links--collapsed': collapsed }">
      <RouterLink id="home" to="/home" class="menu-item tw:no-underline!" :class="{ 'menu-item--active': isActive('/home') }">
        <IconGoogle name="home" />
        <span class="menu-item-label">{{ $t("menu.home") }}</span>
      </RouterLink>

      <RouterLink v-if="getIsAdmin" id="dashboard" to="/dashboard" class="menu-item tw:no-underline!" :class="{ 'menu-item--active': isActive('/dashboard') }">
        <IconGoogle name="trending_up" />
        <span class="menu-item-label">{{ $t("menu.dashboard") }}</span>
      </RouterLink>

      <RouterLink id="certificado" to="/certificates" class="menu-item tw:no-underline!" :class="{ 'menu-item--active': isActive('/certificates') }">
        <IconGoogle name="workspace_premium" />
        <span class="menu-item-label">{{ $t("menu.certificado") }}</span>
      </RouterLink>

      <RouterLink id="my-courses" to="/my-courses" class="menu-item tw:no-underline!" :class="{ 'menu-item--active': isActive('/my-courses') }">
        <IconGoogle name="school" />
        <span class="menu-item-label">{{ $t("menu.logout.my_courses") }}</span>
      </RouterLink>

      <RouterLink id="live-lessons" to="/live-lessons" class="menu-item tw:no-underline!" :class="{ 'menu-item--active': isActive('/live-lessons') }">
        <IconGoogle name="live_tv" />
        <span class="menu-item-label">{{ $t("menu.logout.live_lessons") }}</span>
      </RouterLink>

      <RouterLink
        v-if="(getIsAdmin || getIsGamificationActive)" id="gamification" data-v="T" to="/gamification"
        class="menu-item tw:no-underline!" :class="{ 'menu-item--active': isActive('/gamification') }">
        <IconGoogle name="emoji_events" />
        <span class="menu-item-label">{{ $t("menu.gamification") }}</span>
      </RouterLink>

      <RouterLink
        v-if="getIsAdmin && getSettings?.comment_review" id="comunidade"
        to="/comunidade" class="menu-item tw:no-underline!" :class="{ 'menu-item--active': isActive('/comunidade') }">
        <IconGoogle name="forum" />
        <span class="menu-item-label">{{ $t("menu.comments") }}</span>
      </RouterLink>

      <RouterLink v-if="getIsAdmin" id="membros" to="/members" class="menu-item tw:no-underline!" :class="{ 'menu-item--active': isActive('/members') }">
        <IconGoogle name="group" />
        <span class="menu-item-label">{{ $t("menu.members") }}</span>
      </RouterLink>

      <RouterLink v-if="getIsAdmin" id="combos" to="/combos" class="menu-item tw:no-underline!" :class="{ 'menu-item--active': isActive('/combos') }">
        <IconGoogle name="inventory_2" />
        <span class="menu-item-label">{{ $t("menu.combos") }}</span>
      </RouterLink>

      <div
        v-if="getIsAdmin" id="media-management" class="tw:cursor-pointer menu-item tw:no-underline!"
        @click="showModalUpload">
        <IconGoogle name="perm_media" />
        <span class="menu-item-label">{{ $t("menu.media_management") }}</span>
      </div>

      <RouterLink v-if="exercisesAllowed" id="exercises" to="/exercicios/fisica" class="menu-item tw:no-underline!" :class="{ 'menu-item--active': isActive('/exercicios') }">
        <IconGoogle name="straighten" />
        <span class="menu-item-label">{{ $t("menu.exercises") }}</span>
      </RouterLink>

      <RouterLink v-if="studyScheduleAllowed" id="study-schedule" to="/cronograma" class="menu-item tw:no-underline!" :class="{ 'menu-item--active': isActive('/cronograma') }">
        <IconGoogle name="calendar_today" />
        <span class="menu-item-label">{{ $t("menu.study_schedule") }}</span>
      </RouterLink>

      <RouterLink v-if="intensivaoAllowed" id="study-schedule" to="/intensivao" class="menu-item tw:no-underline!" :class="{ 'menu-item--active': isActive('/intensivao') }">
        <IconGoogle name="calendar_today" />
        <span class="menu-item-label">{{ $t("menu.intensivao") }}</span>
      </RouterLink>

      <SidebarMenuCustom
        v-for="(link, index) in customLinks"
        :key="index"
        :index="index"
        :icon="link.icon"
        :label="link.label"
        :url="link.url"
        :newtab="link.newtab"
        :collapsed="collapsed"
      />

      <a
        v-if="getSettings?.helper_data && !getSettings?.external_help" id="helper"
        :href="`https://reclamacao.voompcreators.${url_domain}/`" target="_blank" class="menu-item tw:no-underline!">
        <IconGoogle name="contact_support" />
        <span class="menu-item-label">{{ $t("menu.help") }}</span>
      </a>

      <a
        v-if="getSettings?.helper_data && getSettings?.external_help" id="helper"
        :href="getSettings?.external_help_link" target="_blank" class="menu-item tw:no-underline!">
        <IconGoogle name="contact_support" />
        <span class="menu-item-label">{{ $t("menu.help") }}</span>
      </a>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { isWithinInterval, parseISO } from "date-fns";
import SidebarMenuCustom from "./SidebarMenuCustom.vue";
import { modal } from "@/events";
import { RouterLink, useRoute } from "vue-router";
import { computed } from "vue";
import { useStateStore } from "@/store/state.store";
import { useAuthStore } from "@/store/auth.store";
import { useConfigStore } from "@/store/config.store";
import { useUserStore } from "@/store/user.store";

defineProps<{ collapsed?: boolean }>();

const authStore = useAuthStore()
const stateStore = useStateStore()
const configStore = useConfigStore()
const userStore = useUserStore()
const route = useRoute()

const url_domain = import.meta.env.VUE_URL_DOMAIN

const getSettings = computed(() => configStore.getSettings)
const getIsAdmin = computed(() => authStore.isAdmin())
const getIsGamificationActive = computed(() => configStore.getIsGamificationActive)

const customLinks = computed(() => stateStore.customLinks)

function isActive(path: string): boolean {
  return route.path === path || route.path.startsWith(path + '/')
}

const exercisesAllowed = computed(() => {
  if (
    userStore.metasSite &&
    userStore.metasSite.exercises_allowed
  ) {
    return userStore.metasSite.exercises_allowed === "1";
  } else return null;
})

const studyScheduleAllowed = computed(() => {
  if (
    userStore.metasSite &&
    userStore.metasSite.study_schedule_allowed
  ) {
    return userStore.metasSite.study_schedule_allowed === "1";
  } else return null;
})

const intensivaoAllowed = computed(() => {
  const isInInterval = isWithinInterval(
    new Date(),
    { start: parseISO('2024-09-30'), end: parseISO('2024-11-11') }
  )
  return isInInterval && studyScheduleAllowed.value
})

async function showModalUpload() {
  modal.show("MediaManagement", {
    type: "any",
  })
}
</script>

<style lang="scss" scoped>
.skeleton-menu-item {
  height: 20px;
  width: 140px;
  margin: 0.625rem 0rem;
}

.menu {
  &-content {
    width: 100%;
  }

  &-links {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 0;
    flex: 1 0 0;
    width: 100%;

    &--collapsed {
      align-items: center;

      .menu-item {
        justify-content: center;
        padding: 0.625rem;
        width: auto;
        gap: 0;
      }

      .menu-item-label {
        display: none;
      }
    }
  }

  &-item-label {
    overflow: hidden;
    white-space: nowrap;
  }

  &-item {
    display: flex;
    flex-direction: row;
    cursor: pointer;
    width: 100%;
    padding: 0.625rem 0.5rem;
    border-radius: 8px;
    color: var(--fontcolor);
    align-items: center;
    gap: 1rem;
    font-size: 0.875rem;
    font-style: normal;
    font-weight: 600;
    line-height: 120%;
    transition: color 0.2s ease, background-color 0.2s ease;
    white-space: nowrap;
    overflow: hidden;

    :deep(svg) {
      width: 1.25rem;
      height: 1.25rem;
      flex-shrink: 0;
    }

    &:hover {
      background-color: var(--sidebar-item-hover-color);
    }
  }
}
</style>

<style>
.menu-item.menu-item--active {
  color: var(--maincolor) !important;
  background-color: color-mix(in srgb, var(--maincolor) 15%, transparent) !important;
}

.menu-item.menu-item--active .svg-inner path {
  fill: var(--maincolor) !important;
}

.menu-item.menu-item--active span[data-testid="icon-google"] {
  color: var(--maincolor) !important;
}
</style>
